import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import axios from 'axios';
import { toast } from 'react-toastify';
import { generateForm } from '../../helpers/functions';
import SingleCustomAutocomplete from '../CustomAutocomplete/SingleCustomAutocomplete';
import CustomMuiDataTable from '../CustomMuiDataTable';
import Swal from 'sweetalert2';

const StatusTab = ({ jobId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [addStatusVisible, setAddStatusVisible] = useState(false);
  const [formData, setFormData] = useState({
    job_id: jobId,
    status_id: null,
    date: "",
    description: "",
    id: -1,
  });

  useEffect(() => {
    if (jobId > 0) {
      loadStatuses();
      getAvailableStatuses();
    }
  }, [jobId]);

  const loadStatuses = () => {
    setLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_API}/admin/jobs/getStatuses/${jobId}`,
    })
      .then((response) => {
        setStatusData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        toast.error(`Error loading statuses`);
        setLoading(false);
      });
  };

  const getAvailableStatuses = () => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/statuses/get`, {})
      .then(function (response) {
        setAvailableStatuses(response.data);
      })
      .catch(function (error) {
        toast.error("Error loading available statuses");
        console.log(error);
      });
  };

  const handleInputChange = (e) => {
    const { id, value, checked, type } = e.target;
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [id]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };

  const customOnChange = (newValue, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSaveStatus = () => {
    setLoading(true);
    const submitFormData = generateForm(formData);

    if (formData.id > 0) {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/admin/jobs/status/update`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Status updated successfully`);
          setLoading(false);
          setAddStatusVisible(false);
          resetForm();
          loadStatuses();
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`Error While Saving`);
        });
    } else {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/admin/jobs/status/add`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Status added successfully`);
          setLoading(false);
          setAddStatusVisible(false);
          resetForm();
          loadStatuses();
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`Error While Saving`);
        });
    }
  };

  const resetForm = () => {
    setFormData({
      job_id: jobId,
      status_id: null,
      date: "",
      description: "",
      id: -1,
    });
  };

  const deleteStatus = (meta) => {
    const id = meta.rowData[0];
    Swal.fire({
      title: "Please Confirm Your Action ?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      icon: "warning",
    }).then((result) => {
      if (result.isConfirmed) {
        axios({
          method: "post",
          url: `${process.env.REACT_APP_API}/admin/jobs/status/delete`,
          data: { id: id },
        })
          .then((response) => {
            toast.success(`Status deleted successfully`);
            loadStatuses();
          })
          .catch((error) => {
            toast.error(`Error deleting status`);
          });
      }
    });
  };

  const statusColumns = [
    {
      name: "id",
      label: "ID",
    },
    {
      name: "status",
      label: "Status",
    },
    {
      name: "date",
      label: "Follow Up Date",
    },
    {
      name: "description",
      label: "Description",
    },
    {
      name: "created_at",
      label: "Created",
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (value, meta) => {
          return (
            <div>
              <button
                className="btn btn-danger mx-1"
                onClick={() => {
                  deleteStatus(meta);
                }}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
  ];

  const statusOptions = {
    filter: true,
    rowsPerPage: 50,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: "none",
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress />
        ) : (
          "There is no matching data to display"
        ),
      },
    },
  };

  return (
    <div>
      <div className="mb-4">
        <button
          className="btn btn-primary"
          onClick={() => setAddStatusVisible(true)}
        >
          Add New Status
        </button>
      </div>

      <CustomMuiDataTable
        title={"Job Statuses"}
        data={statusData}
        columns={statusColumns}
        options={statusOptions}
      />

      {/* Add/Edit Status Modal */}
      {addStatusVisible && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{formData.id > 0 ? "Update" : "Add"} Status</h4>
              <button
                type="button"
                className="close-modal-button"
                onClick={() => setAddStatusVisible(false)}
              >
                &times;
              </button>
            </div>

            <div className="row">
              <div className="col-sm-6">
                <SingleCustomAutocomplete
                  filedName="status_id"
                  label="Status"
                  list={availableStatuses}
                  value={formData.status_id}
                  listKey="id"
                  description="name"
                  customOnChange={customOnChange}
                />
              </div>

              <div className="col-sm-6">
                <div className="form-group">
                  <div className="dateView">
                    <label
                      style={{
                        fontSize: "18px",
                        marginTop: "10px",
                        marginRight: "10px",
                      }}
                      htmlFor="date"
                    >
                      Follow up Date
                    </label>
                    <input
                      type="date"
                      style={{
                        width: 200,
                        height: 50,
                        border: "1px solid #D3D3D3",
                        borderRadius: 5,
                      }}
                      id="date"
                      value={formData.date}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-sm-12">
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    id="description"
                    onChange={handleInputChange}
                    value={formData.description}
                  />
                </div>
              </div>

              <div className="col-sm-12 mt-3">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setAddStatusVisible(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveStatus}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusTab; 